import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Crown, Star, Award, Zap, Check, ChevronRight, Download,
  Users, TrendingUp, Globe, Mail, Phone, Building2, Calendar,
  Target, Megaphone, BarChart3, Gift, Shield, Rocket,
  ArrowRight, CheckCircle2, Clock, FileText, ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sponsorship Tiers
const TIERS = [
  {
    id: 'bronze',
    name: 'Bronze Partner',
    icon: Award,
    color: 'from-amber-600 to-amber-800',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-700',
    establishmentFee: 500,
    monthlyFee: 500,
    features: [
      'Logo on Puppoto homepage',
      'Monthly social media shoutout',
      'Business listing in directory',
      '500 app impressions/month',
      'Quarterly performance report',
      'Partner badge on your website'
    ],
    ideal: 'Local pet shops, groomers, dog walkers'
  },
  {
    id: 'silver',
    name: 'Silver Partner',
    icon: Star,
    color: 'from-slate-400 to-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-300',
    textColor: 'text-slate-600',
    establishmentFee: 1000,
    monthlyFee: 1000,
    popular: true,
    features: [
      'Everything in Bronze, plus:',
      'Featured listing with photos',
      'Weekly social media features',
      '2,500 app impressions/month',
      'Push notification campaigns (2/mo)',
      'Monthly performance reports',
      'Dedicated account manager',
      'Event sponsorship opportunities'
    ],
    ideal: 'Veterinary clinics, pet food brands, training academies'
  },
  {
    id: 'gold',
    name: 'Gold Partner',
    icon: Crown,
    color: 'from-yellow-500 to-amber-500',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-300',
    textColor: 'text-yellow-700',
    establishmentFee: 2500,
    monthlyFee: 2500,
    features: [
      'Everything in Silver, plus:',
      'Premium homepage placement',
      'Daily social media presence',
      'Unlimited app impressions',
      'Push notification campaigns (8/mo)',
      'In-app exclusive offers section',
      'Real-time analytics dashboard',
      'Co-branded content creation',
      'First access to new features',
      'VIP event invitations',
      'Exclusive category lock-out option'
    ],
    ideal: 'National brands, insurance companies, major retailers'
  }
];

// Sales Roadmap Phases
const ROADMAP_PHASES = [
  {
    phase: 1,
    title: 'Onboarding',
    duration: 'Week 1-2',
    icon: Rocket,
    tasks: [
      'Welcome call & strategy session',
      'Brand asset collection',
      'Account setup & integration',
      'Initial campaign planning'
    ]
  },
  {
    phase: 2,
    title: 'Launch',
    duration: 'Week 3-4',
    icon: Megaphone,
    tasks: [
      'Go-live announcement',
      'First social media feature',
      'Directory listing activation',
      'Initial performance baseline'
    ]
  },
  {
    phase: 3,
    title: 'Growth',
    duration: 'Month 2-3',
    icon: TrendingUp,
    tasks: [
      'Campaign optimization',
      'A/B testing promotions',
      'Engagement analysis',
      'Strategy refinement'
    ]
  },
  {
    phase: 4,
    title: 'Scale',
    duration: 'Month 4+',
    icon: BarChart3,
    tasks: [
      'Performance review',
      'ROI reporting',
      'Expansion opportunities',
      'Renewal discussion'
    ]
  }
];

// Advertising Schedule
const AD_SCHEDULE = [
  { day: 'Monday', bronze: 'Directory listing', silver: 'Social post', gold: 'Homepage feature + Social' },
  { day: 'Tuesday', bronze: '-', silver: 'Push notification', gold: 'In-app offer + Push' },
  { day: 'Wednesday', bronze: '-', silver: 'Directory boost', gold: 'Social post + Story' },
  { day: 'Thursday', bronze: '-', silver: '-', gold: 'Email newsletter feature' },
  { day: 'Friday', bronze: 'Social mention', silver: 'Social post', gold: 'Homepage + Social + Push' },
  { day: 'Weekend', bronze: '-', silver: 'Directory boost', gold: 'Premium placement all day' }
];

// Platform Stats
const PLATFORM_STATS = [
  { label: 'Active Users', value: '50,000+', icon: Users },
  { label: 'Monthly Sessions', value: '500K+', icon: TrendingUp },
  { label: 'Countries', value: '16', icon: Globe },
  { label: 'Engagement Rate', value: '78%', icon: Target }
];

const TierCard = ({ tier, onSelect }) => {
  const Icon = tier.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className={`relative rounded-3xl p-6 ${tier.bgColor} border-2 ${tier.borderColor} ${
        tier.popular ? 'ring-2 ring-[#FF7555] ring-offset-2' : ''
      }`}
    >
      {tier.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#FF7555] text-white text-sm font-semibold rounded-full">
          Most Popular
        </div>
      )}
      
      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tier.color} flex items-center justify-center mb-4`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
      
      <h3 className="text-2xl font-bold text-[#1A1817] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
        {tier.name}
      </h3>
      
      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold text-[#1A1817]">${tier.establishmentFee.toLocaleString()}</span>
          <span className="text-[#7A7571]">setup</span>
        </div>
        <div className="flex items-baseline gap-1 mt-1">
          <span className="text-xl font-semibold text-[#1A1817]">+ ${tier.monthlyFee.toLocaleString()}</span>
          <span className="text-[#7A7571]">/month</span>
        </div>
      </div>
      
      <p className="text-sm text-[#7A7571] mb-4 italic">
        Ideal for: {tier.ideal}
      </p>
      
      <ul className="space-y-2 mb-6">
        {tier.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm">
            <Check className={`w-4 h-4 mt-0.5 ${tier.textColor} flex-shrink-0`} />
            <span className="text-[#1A1817]">{feature}</span>
          </li>
        ))}
      </ul>
      
      <Button
        onClick={() => onSelect(tier)}
        className={`w-full h-12 rounded-xl font-semibold bg-gradient-to-r ${tier.color} text-white hover:opacity-90`}
      >
        Get Started
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </motion.div>
  );
};

const RoadmapCard = ({ phase, isLast }) => {
  const Icon = phase.icon;
  
  return (
    <div className="relative flex gap-4">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-6 top-14 w-0.5 h-[calc(100%-20px)] bg-[#EBE8E0]" />
      )}
      
      {/* Icon */}
      <div className="w-12 h-12 rounded-xl bg-[#FF7555] flex items-center justify-center flex-shrink-0 z-10">
        <Icon className="w-6 h-6 text-white" />
      </div>
      
      {/* Content */}
      <div className="flex-1 pb-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-sm font-semibold text-[#FF7555]">Phase {phase.phase}</span>
          <span className="text-xs text-[#7A7571] bg-[#F5F3EE] px-2 py-0.5 rounded-full">
            {phase.duration}
          </span>
        </div>
        <h4 className="text-lg font-bold text-[#1A1817] mb-2">{phase.title}</h4>
        <ul className="space-y-1">
          {phase.tasks.map((task, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm text-[#7A7571]">
              <CheckCircle2 className="w-4 h-4 text-[#4E876C]" />
              {task}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const InquiryForm = ({ selectedTier, onClose }) => {
  const [form, setForm] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.businessName || !form.email || !form.contactName) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/puppoto/sponsor-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          tier: selectedTier?.id || 'general',
          tierName: selectedTier?.name || 'General Inquiry'
        })
      });
      
      if (response.ok) {
        toast.success('Inquiry submitted! We\'ll be in touch within 24 hours.');
        onClose();
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      // Still show success - we'll store locally
      toast.success('Inquiry received! We\'ll contact you shortly.');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Partner Inquiry
            </h3>
            {selectedTier && (
              <p className="text-sm text-[#FF7555] font-medium mt-1">
                {selectedTier.name} - ${selectedTier.establishmentFee} setup + ${selectedTier.monthlyFee}/mo
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-1">Business Name *</label>
            <Input
              value={form.businessName}
              onChange={e => setForm(p => ({ ...p, businessName: e.target.value }))}
              placeholder="Your Company Pty Ltd"
              className="rounded-xl"
              data-testid="sponsor-business-name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-1">Contact Name *</label>
            <Input
              value={form.contactName}
              onChange={e => setForm(p => ({ ...p, contactName: e.target.value }))}
              placeholder="John Smith"
              className="rounded-xl"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-1">Email *</label>
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="john@company.com"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-1">Phone</label>
              <Input
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="0400 000 000"
                className="rounded-xl"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-1">Website</label>
            <Input
              value={form.website}
              onChange={e => setForm(p => ({ ...p, website: e.target.value }))}
              placeholder="www.yourcompany.com.au"
              className="rounded-xl"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-1">Message</label>
            <textarea
              value={form.message}
              onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
              placeholder="Tell us about your business and goals..."
              className="w-full px-4 py-3 rounded-xl border border-[#EBE8E0] resize-none h-24"
            />
          </div>
          
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl font-semibold"
            data-testid="submit-sponsor-inquiry"
          >
            {submitting ? 'Submitting...' : 'Submit Inquiry'}
          </Button>
        </form>
      </motion.div>
    </motion.div>
  );
};

const PupSponsors = () => {
  const navigate = useNavigate();
  const [showInquiry, setShowInquiry] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  const handleTierSelect = (tier) => {
    setSelectedTier(tier);
    setShowInquiry(true);
  };

  const handleDownloadMediaKit = () => {
    // Create a simple media kit content
    toast.success('Media Kit downloading...');
    // In production, this would link to an actual PDF
    window.open(`${API_URL}/api/puppoto/media-kit`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FDFCF9]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBE8E0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/puppoto')}
            className="flex items-center gap-2 text-[#1A1817] hover:text-[#FF7555] transition"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">Back to Puppoto</span>
          </button>
          
          <div className="flex items-center gap-2">
            <span className="text-2xl">🐕</span>
            <span className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Puppoto Partners
            </span>
          </div>
          
          <Button
            onClick={() => setShowInquiry(true)}
            className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
          >
            <Mail className="w-4 h-4 mr-2" />
            Contact Us
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#FF7555]/10 via-white to-[#4E876C]/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF7555]/10 text-[#FF7555] rounded-full text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              Partner with Australia's Fastest Growing Dog Platform
            </span>
            
            <h1 className="text-4xl md:text-5xl font-bold text-[#1A1817] mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Grow Your Business with{' '}
              <span className="text-[#FF7555]">Puppoto</span>
            </h1>
            
            <p className="text-lg text-[#7A7571] mb-8 max-w-2xl mx-auto">
              Connect with 50,000+ engaged dog lovers. Our sponsorship packages deliver 
              measurable ROI with dedicated support and transparent reporting.
            </p>
            
            {/* Platform Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {PLATFORM_STATS.map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <div key={idx} className="bg-white rounded-2xl p-4 shadow-sm border border-[#EBE8E0]">
                    <Icon className="w-6 h-6 text-[#FF7555] mx-auto mb-2" />
                    <div className="text-2xl font-bold text-[#1A1817]">{stat.value}</div>
                    <div className="text-sm text-[#7A7571]">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Partnership Tiers
            </h2>
            <p className="text-[#7A7571] max-w-xl mx-auto">
              Choose the tier that fits your business. All packages include a one-time establishment 
              fee plus monthly recurring - building sustainable partnerships.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {TIERS.map(tier => (
              <TierCard key={tier.id} tier={tier} onSelect={handleTierSelect} />
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-sm text-[#7A7571]">
              <Shield className="w-4 h-4 inline mr-1" />
              All partnerships include a 30-day performance guarantee. Not seeing results? We'll work with you to fix it.
            </p>
          </div>
        </div>
      </section>

      {/* Advertising Schedule */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Weekly Advertising Schedule
            </h2>
            <p className="text-[#7A7571]">
              Pippa's roadmap for consistent brand exposure across all tiers
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#F5F3EE]">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1817] rounded-tl-xl">Day</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-amber-700">
                    <Award className="w-4 h-4 inline mr-1" />
                    Bronze
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">
                    <Star className="w-4 h-4 inline mr-1" />
                    Silver
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-yellow-700 rounded-tr-xl">
                    <Crown className="w-4 h-4 inline mr-1" />
                    Gold
                  </th>
                </tr>
              </thead>
              <tbody>
                {AD_SCHEDULE.map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FDFCF9]'}>
                    <td className="px-4 py-3 font-medium text-[#1A1817]">{row.day}</td>
                    <td className="px-4 py-3 text-sm text-[#7A7571]">{row.bronze}</td>
                    <td className="px-4 py-3 text-sm text-[#7A7571]">{row.silver}</td>
                    <td className="px-4 py-3 text-sm text-[#7A7571]">{row.gold}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Sales Roadmap */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Partner Success Roadmap
            </h2>
            <p className="text-[#7A7571]">
              Our proven 4-phase approach to maximize your ROI
            </p>
          </div>
          
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#EBE8E0]">
            {ROADMAP_PHASES.map((phase, idx) => (
              <RoadmapCard 
                key={phase.phase} 
                phase={phase} 
                isLast={idx === ROADMAP_PHASES.length - 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Media Kit Download */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#FF7555] to-[#E5684A]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-90" />
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Download Our Media Kit
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Get all the details - platform demographics, case studies, pricing breakdown, 
            and brand guidelines in one comprehensive PDF.
          </p>
          <Button
            onClick={handleDownloadMediaKit}
            size="lg"
            className="bg-white text-[#FF7555] hover:bg-gray-100 rounded-xl font-semibold px-8"
            data-testid="download-media-kit"
          >
            <Download className="w-5 h-5 mr-2" />
            Download Media Kit (PDF)
          </Button>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Why Partner with Puppoto?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: 'Hyper-Targeted Audience',
                description: 'Reach dog owners actively looking for products and services. No wasted impressions.'
              },
              {
                icon: TrendingUp,
                title: 'Measurable Results',
                description: 'Real-time analytics dashboard. Track impressions, clicks, and conversions.'
              },
              {
                icon: Gift,
                title: 'Residual Income Model',
                description: 'Establishment fee + monthly recurring builds sustainable, long-term partnerships.'
              },
              {
                icon: Users,
                title: 'Engaged Community',
                description: '78% engagement rate. Our users don\'t just scroll - they interact.'
              },
              {
                icon: Globe,
                title: 'Multi-Language Platform',
                description: '16 languages supported. Reach diverse communities across Australia.'
              },
              {
                icon: Shield,
                title: 'Brand Safety',
                description: 'Premium, family-friendly environment. Your brand is in safe paws.'
              }
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="text-center"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#FF7555]/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-[#FF7555]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1A1817] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#7A7571]">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Ready to Reach 50,000+ Dog Lovers?
          </h2>
          <p className="text-[#7A7571] mb-8">
            Let's discuss how Puppoto can help grow your business. 
            Our team will respond within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setShowInquiry(true)}
              size="lg"
              className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl font-semibold px-8"
            >
              <Mail className="w-5 h-5 mr-2" />
              Start Partnership Inquiry
            </Button>
            <Button
              onClick={() => window.location.href = 'tel:+61400000000'}
              size="lg"
              variant="outline"
              className="border-[#1A1817] text-[#1A1817] hover:bg-[#1A1817] hover:text-white rounded-xl font-semibold px-8"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-[#EBE8E0] bg-white">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">🐕</span>
            <span className="font-bold text-[#1A1817]">Puppoto Partners</span>
          </div>
          <p className="text-sm text-[#7A7571]">
            © 2026 Puppoto. Part of the Lionheart Digital family.
          </p>
          <div className="flex items-center gap-4">
            <a href="mailto:partners@puppoto.com" className="text-sm text-[#7A7571] hover:text-[#FF7555]">
              partners@puppoto.com
            </a>
          </div>
        </div>
      </footer>

      {/* Inquiry Modal */}
      {showInquiry && (
        <InquiryForm
          selectedTier={selectedTier}
          onClose={() => {
            setShowInquiry(false);
            setSelectedTier(null);
          }}
        />
      )}
    </div>
  );
};

export default PupSponsors;
